import { useEffect, useRef, useState } from 'react';
import defaultAlarmSound from '../assets/sounds/doorbell.mp3';

// Shared version of the looping-alarm pattern duplicated in DeliveryOrders.jsx and
// ModalSetting.jsx: creates one HTMLAudioElement, loops it while active, and handles browsers
// that block autoplay until a user gesture (showAudioUnlock banner + handleUnlockAudio).
export default function useAlarmSound(src = defaultAlarmSound) {
    const alarmAudioRef = useRef(null);
    const [alarmActive, setAlarmActive] = useState(false);
    const [audioUnlocked, setAudioUnlocked] = useState(false);
    const [showAudioUnlock, setShowAudioUnlock] = useState(false);
    // Mirrors alarmActive for the pointerdown listener below: that listener is attached once (deps
    // are just [src]) so a closure over the `alarmActive` state variable itself would go stale after
    // the first play/stop and never fire again - reading a ref instead always sees the live value.
    const alarmActiveRef = useRef(false);

    useEffect(() => {
        const audio = new Audio(src);
        audio.loop = true;
        audio.preload = 'auto';
        audio.volume = 1.0;
        alarmAudioRef.current = audio;

        const stopOnUserInteraction = () => {
            if (alarmActiveRef.current) stopAlarm();
        };
        window.addEventListener('pointerdown', stopOnUserInteraction, { passive: true });

        // Try to unlock audio right away instead of always waiting for a click. Once this
        // origin has enough engagement (the operator has clicked "Habilitar sonido" before on
        // this browser/device), Chrome's autoplay policy lets play() succeed without a gesture,
        // so the banner only shows up when the browser actually rejects the attempt.
        (async () => {
            try {
                await audio.play();
                audio.pause();
                audio.currentTime = 0;
                setAudioUnlocked(true);
                setShowAudioUnlock(false);
            } catch (err) {
                setAudioUnlocked(false);
                setShowAudioUnlock(true);
            }
        })();

        return () => {
            window.removeEventListener('pointerdown', stopOnUserInteraction);
            try { audio.pause(); } catch (_) { }
            alarmAudioRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    const startAlarm = async () => {
        const a = alarmAudioRef.current;
        if (!a) return;
        a.currentTime = 0;
        try {
            await a.play();
            setAudioUnlocked(true);
            setShowAudioUnlock(false);
            setAlarmActive(true);
            alarmActiveRef.current = true;
        } catch (err) {
            setAudioUnlocked(false);
            setShowAudioUnlock(true);
            setAlarmActive(false);
            alarmActiveRef.current = false;
        }
    };

    const stopAlarm = () => {
        const a = alarmAudioRef.current;
        alarmActiveRef.current = false;
        setAlarmActive(false);
        if (!a) return;
        try { a.pause(); } catch (_) { }
        a.currentTime = 0;
    };

    const handleUnlockAudio = async () => {
        const a = alarmAudioRef.current;
        if (!a) return;
        try {
            await a.play();
            a.pause();
            setAudioUnlocked(true);
            setShowAudioUnlock(false);
        } catch (_) {
            setAudioUnlocked(false);
            setShowAudioUnlock(true);
        }
    };

    return { alarmActive, showAudioUnlock, startAlarm, stopAlarm, handleUnlockAudio };
}
